'use client'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useStmWeeksWithStats } from '@/features/stm/hooks/use-stm-weeks'
import {
  useStmCreateBlock,
  useStmUpdateBlock,
} from '@/features/stm/hooks/use-stm-groups-blocks'
import { BLOCK_SEVERITIES } from '@/features/stm/lib/types'
import type { Block, BlockSeverity } from '@/features/stm/lib/types'
import { formatDateRange, doesOverlap } from '@/features/stm/lib/date-utils'
import { Ban, AlertTriangle } from 'lucide-react'

const formSchema = z
  .object({
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().min(1, 'End date is required'),
    reason: z.string().min(1, 'Reason is required').max(200),
    severity: z.enum(BLOCK_SEVERITIES as [BlockSeverity, ...BlockSeverity[]]),
  })
  .refine((d) => d.start_date <= d.end_date, {
    message: 'End date must be after start date',
    path: ['end_date'],
  })

type FormValues = z.infer<typeof formSchema>

interface BlockFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  block?: Block | null
  defaultStartDate?: string
  defaultEndDate?: string
  regionId?: number | undefined | null
}

export function BlockFormDialog({
  open,
  onOpenChange,
  block,
  defaultStartDate,
  defaultEndDate,
  regionId,
}: BlockFormDialogProps) {
  const createBlock = useStmCreateBlock(regionId ?? null)
  const updateBlock = useStmUpdateBlock()
  const { data: weeks } = useStmWeeksWithStats(regionId ?? null)
  const isEditing = !!block

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      start_date: defaultStartDate || '',
      end_date: defaultEndDate || '',
      reason: '',
      severity: 'Hard Block',
    },
  })

  const watchedStart = form.watch('start_date')
  const watchedEnd = form.watch('end_date')
  const affectedWeeks = (weeks || []).filter(
    (w) => watchedStart && watchedEnd && doesOverlap(watchedStart, watchedEnd, w.week_start, w.week_end)
  )

  useEffect(() => {
    if (block) {
      form.reset({
        start_date: block.start_date,
        end_date: block.end_date,
        reason: block.reason,
        severity: block.severity,
      })
    } else {
      form.reset({
        start_date: defaultStartDate || '',
        end_date: defaultEndDate || '',
        reason: '',
        severity: 'Hard Block',
      })
    }
  }, [block, defaultStartDate, defaultEndDate, form, open])

  const onSubmit = async (values: FormValues) => {
    const data = {
      start_date: values.start_date,
      end_date: values.end_date,
      reason: values.reason,
      severity: values.severity,
    }
    if (isEditing) {
      await updateBlock.mutateAsync({ id: block.id, ...data })
    } else {
      await createBlock.mutateAsync(data)
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Block' : 'Add Block'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason *</FormLabel>
                  <FormControl>
                    <Input placeholder="Maintenance, Staff Retreat, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="severity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Severity *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Hard Block">
                        <span className="flex items-center gap-2">
                          <Ban className="h-4 w-4 text-destructive" />
                          Hard Block (Unavailable)
                        </span>
                      </SelectItem>
                      <SelectItem value="Soft Block">
                        <span className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          Soft Block (Discouraged)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Hard blocks mark the base as unavailable. Soft blocks show a warning.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {affectedWeeks.length > 0 && (
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-sm font-medium mb-2">Affected Weeks ({affectedWeeks.length})</p>
                <div className="flex flex-wrap gap-1">
                  {affectedWeeks.slice(0, 6).map((w) => (
                    <span key={w.id} className="text-xs px-2 py-1 bg-secondary rounded-full">
                      {formatDateRange(w.week_start, w.week_end)}
                    </span>
                  ))}
                  {affectedWeeks.length > 6 && (
                    <span className="text-xs px-2 py-1 text-muted-foreground">
                      +{affectedWeeks.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createBlock.isPending || updateBlock.isPending}>
                {isEditing ? 'Save Changes' : 'Add Block'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
